import { Inject, Injectable, InjectionToken } from '@angular/core';
import { initialize, LDClient, LDContext, LDOptions } from 'launchdarkly-js-client-sdk';
import SessionReplay from '@launchdarkly/session-replay';
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from '../../app/app.config';

export type LDInitializeFn = (envKey: string, context: LDContext, options?: LDOptions) => LDClient;

export const LD_INITIALIZE_FN = new InjectionToken<LDInitializeFn>('LD_INITIALIZE_FN', {
  providedIn: 'root',
  factory: () => initialize,
});

@Injectable({ providedIn: 'root' })
export class LaunchDarklyService {
  private client?: LDClient;
  private initializedUserKey?: string;

  constructor(
    private readonly conn: ConnectionService,
    private readonly appConfig: AppConfig,
    @Inject(LD_INITIALIZE_FN) private readonly ldInitialize: LDInitializeFn,
  ) {}

  init(user: { get(field: string): string | undefined }): void {
    if (!user) return;

    const clientId = this.getClientId();
    const userKey = user.get('username');
    if (!clientId || !userKey) return;

    if (this.client && this.initializedUserKey === userKey) return;

    const context: LDContext = {
      kind: 'user',
      key: userKey,
    };

    if (this.client) {
      this.client.identify(context).catch(() => {});
      this.initializedUserKey = userKey;
      return;
    }

    this.client = this.ldInitialize(clientId, context, {
      plugins: [
        new SessionReplay({
          privacySetting: 'default',
          blockClass: 'ld-session-block',
        }),
      ],
    });

    this.initializedUserKey = userKey;
    this.client.waitForInitialization().catch(() => {});
  }

  getClientId(): string {
    return this.conn.isDevelopmentEnvironment ?
      this.appConfig.Shared?.LaunchDarkly?.CLIENT_ID_DEV
      : this.appConfig.Shared?.LaunchDarkly?.CLIENT_ID_PROD;
  }
}
